import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { toast } from 'react-toastify';

export default function PromoShowcaseStockMediaDialog({
  open,
  onClose,
  instanceUniqueId,
  initialQuery = 'business promotional products',
  onSelect,
}) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
  const [query, setQuery] = useState(initialQuery);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [providerMeta, setProviderMeta] = useState(null);

  const search = async (nextQuery = query) => {
    const q = String(nextQuery || '').trim();
    if (!q || !instanceUniqueId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/media/stock/search`,
        {
          params: { q, orientation: 'landscape', perPage: 24 },
          ...headers,
        }
      );
      setPhotos(Array.isArray(response?.data?.photos) ? response.data.photos : []);
      setProviderMeta(response?.data?.attribution || null);
    } catch (error) {
      setPhotos([]);
      toast.error(error?.response?.data?.message || 'Unable to search stock imagery.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    search(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery, instanceUniqueId]);

  const choose = async (photo) => {
    setImportingId(photo?.id || 'importing');
    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/media/stock/import`,
        {
          provider: photo.provider,
          sourceUrl: photo.largeUrl || photo.originalUrl || photo.landscapeUrl,
          title: photo.alt || 'Showcase stock image',
          alt: photo.alt || '',
          credit: photo.attribution || '',
          creditUrl: photo.photographerUrl || '',
          sourcePageUrl: photo.sourcePageUrl || '',
        },
        headers
      );
      const imported = response?.data || {};
      if (!imported.url) throw new Error('WordPress did not return an imported image URL.');
      onSelect?.({
        url: imported.url,
        alt: imported.alt || photo.alt || '',
        provider: imported.provider || photo.provider,
        credit: imported.credit || photo.attribution || '',
        creditUrl: imported.creditUrl || photo.photographerUrl || '',
        sourcePageUrl: imported.sourcePageUrl || photo.sourcePageUrl || '',
        attachmentId: imported.attachmentId || null,
      });
      toast.success('Stock image imported to WordPress.');
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to import stock image.');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={importingId ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Stock imagery</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Search free stock photos"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  search();
                }
              }}
            />
            <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={() => search()} disabled={loading}>
              Search
            </Button>
          </Box>

          {providerMeta?.providerUrl ? (
            <Typography variant="caption" color="text.secondary">
              Photos provided by{' '}
              <Link href={providerMeta.providerUrl} target="_blank" rel="noreferrer">
                {providerMeta.providerName || 'Pexels'}
              </Link>.
              Selected photos are imported into this Showcase's WordPress Media Library.
            </Typography>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 1.25,
              }}
            >
              {photos.map((photo) => (
                <Box
                  key={photo.id}
                  sx={{
                    border: '1px solid rgba(15,23,42,.12)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: '#fff',
                  }}
                >
                  <Box
                    component="img"
                    src={photo.previewUrl || photo.largeUrl}
                    alt={photo.alt || ''}
                    loading="lazy"
                    sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
                  />
                  <Box sx={{ p: 1, display: 'grid', gap: .65 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }} noWrap>
                      {photo.photographer || 'Pexels photographer'}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!!importingId}
                      onClick={() => choose(photo)}
                    >
                      {importingId === photo.id ? 'Importing...' : 'Use image'}
                    </Button>
                    {photo.sourcePageUrl ? (
                      <Link href={photo.sourcePageUrl} target="_blank" rel="noreferrer" variant="caption">
                        View on Pexels
                      </Link>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!loading && !photos.length ? (
            <Typography variant="body2" color="text.secondary">No images found.</Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!importingId}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
